<template>
  <div class="planos-sidebar-container">
    <!-- CABECERA Y ACCIÓN -->
    <div class="pa-4 d-flex align-center justify-space-between">
      <div class="text-overline grey--text text--darken-1">Mis Planos</div>
      <v-tooltip v-if="puedeEditarPlanos" left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            color="primary"
            fab
            x-small
            depressed
            @click="abrirDialogoCrear"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Añadir nuevo plano</span>
      </v-tooltip>
    </div>

    <!-- BUSCADOR (Opcional pero recomendado para UX) -->
    <div class="px-4 pb-2">
      <v-text-field
        v-model="busquedaLocal"
        prepend-inner-icon="mdi-magnify"
        placeholder="Buscar plano..."
        dense
        outlined
        hide-details
        class="search-input shadow-sm"
      ></v-text-field>
    </div>

    <v-divider class="mx-4 mb-2"></v-divider>

    <!-- LISTA DE PLANOS -->
    <v-list dense nav class="planos-list px-2">
      <template v-if="itemsFiltrados.length > 0">
        <v-list-item-group :value="planoSeleccionado?.id" color="primary">
          <v-list-item
            v-for="plano in itemsFiltrados"
            :key="plano.id"
            :value="plano.id"
            @click="seleccionar(plano)"
            class="plano-item mb-1"
            active-class="plano-item--active"
          >
            <v-list-item-avatar color="primary lighten-5" size="36">
              <v-icon color="primary" small>mdi-floor-plan</v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">
                {{ plano.nombre }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                {{ plano.descripcion || 'Sin descripción' }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <!-- ACCIONES: EDITAR Y ELIMINAR -->
            <v-list-item-action v-if="puedeEditarPlanos" class="d-flex flex-row">
              <v-btn icon x-small @click.stop="prepararEdicion(plano)" class="mr-1">
                <v-icon small color="grey darken-1">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon x-small @click.stop="prepararEliminacion(plano)">
                <v-icon small color="error lighten-1">mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>

            <v-list-item-action v-if="plano.id === planoSeleccionado?.id">
              <v-icon x-small color="primary">mdi-circle</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list-item-group>
      </template>

      <!-- ESTADO VACÍO -->
      <div v-else class="text-center pa-8 grey--text text--lighten-1">
        <v-icon size="48" color="grey lighten-3">mdi-map-search-outline</v-icon>
        <div class="caption mt-2">No se encontraron planos</div>
      </div>
    </v-list>

    <!-- DIÁLOGO DE CREACIÓN / EDICIÓN (Unificado) -->
    <v-dialog v-model="dialogPlano" max-width="450px" persistent>
      <v-card rounded="lg">
        <v-card-title :class="isEditing ? 'warning white--text' : 'primary white--text'">
          <v-icon left color="white">{{ isEditing ? 'mdi-pencil' : 'mdi-plus-box' }}</v-icon>
          {{ isEditing ? 'Editar Plano' : 'Nuevo Plano' }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogPlano = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-text-field
            v-model="formPlano.nombre"
            label="Nombre identificativo"
            outlined dense
            prepend-inner-icon="mdi-tag-outline"
          ></v-text-field>

          <v-textarea
            v-model="formPlano.descripcion"
            label="Descripción (opcional)"
            outlined dense rows="2"
            prepend-inner-icon="mdi-text-align-left"
          ></v-textarea>

          <v-file-input
            v-model="formPlano.archivo"
            :label="isEditing ? 'Cambiar imagen (opcional)' : 'Seleccionar archivo'"
            accept=".pdf,.jpg,.jpeg,.png"
            outlined dense show-size
            prepend-inner-icon="mdi-cloud-upload-outline"
          ></v-file-input>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 grey lighten-5">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogPlano = false">Cancelar</v-btn>
          <v-btn 
            :color="isEditing ? 'warning' : 'primary'" 
            @click="guardarPlano" 
            depressed
            :disabled="!formPlano.nombre || (!isEditing && !formPlano.archivo)"
          >
            {{ isEditing ? 'Actualizar' : 'Crear Plano' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIÁLOGO DE ELIMINACIÓN -->
    <v-dialog v-model="dialogBorrar" max-width="350px">
      <v-card>
        <v-card-title class="text-h6">¿Eliminar plano?</v-card-title>
        <v-card-text>
          Esta acción eliminará el plano <b>{{ planoSeleccionadoParaAccion?.nombre }}</b> y todas sus regiones vinculadas. Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogBorrar = false">Cancelar</v-btn>
          <v-btn color="error" text @click="ejecutarBorrado">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="3000" top right rounded="pill" color="grey darken-3">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';

export default {
  name: "PlanosLista",
  props: {
    modo: Number,
    planosData: { type: Array, required: true },
    planoSeleccionado: { type: Object, default: null },
  },
  data() {
    return {
      dialogPlano: false,
      isEditing: false,
      dialogBorrar: false,
      planoSeleccionadoParaAccion: null,
      
      formPlano: {
        id: null,
        nombre: "",
        descripcion: "",
        archivo: null
      },

      snackbar: false,
      snackbarText: "",
      busquedaLocal: "", // Nuevo para filtro
    }
  },
  computed: {
    ...mapState(["idCliente", "permisos"]),
    puedeEditarPlanos() {
      return (this.permisos || []).includes("editar-planos");
    },
    itemsFiltrados() {
      if (!this.busquedaLocal) return this.planosData;
      return this.planosData.filter(p => 
        p.nombre.toLowerCase().includes(this.busquedaLocal.toLowerCase())
      );
    }
  },
  methods: {
    seleccionar(plano) {
      this.$emit("select", plano);
    },

    abrirDialogoCrear() {
      this.isEditing = false;
      this.formPlano = { id: null, nombre: "", descripcion: "", archivo: null };
      this.dialogPlano = true;
    },

    prepararEdicion(plano) {
      this.isEditing = true;
      this.formPlano = {
        id: plano.id,
        nombre: plano.nombre,
        descripcion: plano.descripcion || "",
        archivo: null // El archivo es opcional en edición
      };
      this.dialogPlano = true;
    },

    prepararEliminacion(plano) {
      this.planoSeleccionadoParaAccion = plano;
      this.dialogBorrar = true;
    },

    async guardarPlano() {
      const formData = new FormData();
      formData.append("nombre", this.formPlano.nombre);
      formData.append("descripcion", this.formPlano.descripcion);
      formData.append("id_cliente", this.idCliente);
      if (this.formPlano.archivo) {
        formData.append("archivo", this.formPlano.archivo);
      }

      try {
        if (this.isEditing) {
          // EDITAR (PUT)
          await axios.put(
            `${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/planos/${this.formPlano.id}`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          this.snackbarText = "Plano actualizado correctamente";
        } else {
          // CREAR (POST)
          await axios.post(
            `${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/planos`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          this.snackbarText = "Plano creado con éxito";
        }
        
        this.dialogPlano = false;
        this.snackbarColor = "success";
        this.snackbar = true;
        this.$emit("plano-creado"); // Notificar al padre para recargar lista
      } catch (error) {
        this.snackbarText = "Error al procesar el plano";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },

    async ejecutarBorrado() {
      try {
        await axios.delete(
          `${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/planos/${this.planoSeleccionadoParaAccion.id}`
        );
        this.dialogBorrar = false;
        this.snackbarText = "Plano eliminado";
        this.snackbarColor = "grey darken-3";
        this.snackbar = true;
        this.$emit("plano-creado"); // Recargar lista
      } catch (error) {
        this.snackbarText = "Error al eliminar";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },
  },
};
</script>

<style scoped>
.planos-sidebar-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
}

.planos-list {
  overflow-y: auto;
  flex-grow: 1;
}

/* Estilo para los ítems de la lista */
.plano-item {
  border-radius: 8px !important;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.plano-item:hover {
  background-color: #f5f7fa !important;
}

/* Estilo para el ítem activo */
.plano-item--active {
  background-color: #e3f2fd !important;
  border: 1px solid #bbdefb !important;
}

.plano-item--active .v-list-item__title {
  color: #1976d2;
  font-weight: 700 !important;
}

/* Estilo buscador */
.search-input >>> .v-input__control {
  min-height: 36px !important;
}

.search-input >>> .v-input__slot {
  background: #f8f9fa !important;
  border: none !important;
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
}

/* Scrollbar fino */
.planos-list::-webkit-scrollbar {
  width: 4px;
}
.planos-list::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 10px;
}

.plano-item .v-list-item__action {
  margin: 0 !important;
  opacity: 0.7;
}

.plano-item:hover .v-list-item__action {
  opacity: 1;
}

/* Evitar que el botón de eliminar sea demasiado grande */
.v-list-item__action .v-btn--icon.v-size--x-small {
  width: 24px;
  height: 24px;
}
</style>